import { FaroMenuItem } from "@components/common/faro-select/faro-menu-item";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import {
  getRoleMenuItemSx,
  RoleMenuItem,
} from "@components/role/role-menu-item";
import {
  ChangeRoleEntityType,
  useChangeRoleBulkDialogContent,
} from "@components/table/members/bulk-actions/bulk-change-role-helper";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import {
  AssignableUpdateCompanyMemberRole,
  SdbMemberTypes,
} from "@custom-types/member-types";
import { Update } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { StringEntityId } from "@store/store-types";
import {
  bulkActionNameSelector,
  selectedEntitiesSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import {
  setBulkActionName,
  updateMany,
  updateOne,
} from "@store/table/table-slice";
import { FetchingItem } from "@store/table/table-slice-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { formatUserRoleType } from "@utils/data-display";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";

interface Props extends BaseCompanyIdProps {
  /**
   * Specifies the type of the entity: either an individual user or a team.
   */
  entityType: "members" | "teams";
}

/** Button allowing to change role of members or teams in company */
export function BulkChangeCompanyRole({
  companyId,
  entityType,
}: Props): JSX.Element {
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);
  const [newRole, setNewRole] =
    useState<FaroCoreApiTypes.UpdateCompanyMemberRole>("MEMBER");

  const currentUser = useAppSelector(currentUserSelector);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const selectedEntities = useAppSelector(selectedEntitiesSelector(entityType));

  const dispatch = useAppDispatch();

  const { bulkDialogContent, successDialogContent } =
    useChangeRoleBulkDialogContent({
      subjectType: "workspace",
      newRole,
      entityType:
        entityType === "members"
          ? ChangeRoleEntityType.member
          : ChangeRoleEntityType.team,
    });

  // Allowing no workspace role for teams
  const assignableRoles =
    entityType === "teams"
      ? AssignableUpdateCompanyMemberRole
      : AssignableUpdateCompanyMemberRole.filter((role) => role !== "MEMBER");

  // Closes the bulk dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);

  function onNewRoleClicked(event: React.MouseEvent<HTMLLIElement>): void {
    dispatch(setBulkActionName("changeRole"));

    const notAllowedChanges: Update<FetchingItem, StringEntityId>[] = [];
    for (const entity of selectedEntities) {
      // Marking entities as not allowed if project and group managers are selected for changing role to member
      if (
        event.currentTarget.dataset.myValue === "MEMBER" &&
        (entity.role === "COMPANY_MANAGER" || entity.role === "PROJECT_MANAGER")
      ) {
        notAllowedChanges.push({
          id: entity.identity,
          changes: {
            status: "not-allowed",
            message: `You cannot change from ${formatUserRoleType(
              entity.role
            )} role to ${formatUserRoleType("MEMBER")}.`,
          },
        });
      }
    }

    if (notAllowedChanges.length) {
      dispatch(updateMany(notAllowedChanges));
    }

    if (currentUser) {
      dispatch(
        updateOne({
          id: currentUser.identity,
          changes: {
            status: "not-allowed",
            message: "You cannot change the role of yourself.",
          },
        })
      );
    }

    // TODO: Check whether it is possible to remove type-casting: https://faro01.atlassian.net/browse/ST-1348
    setNewRole(
      event.currentTarget.dataset
        .myValue as FaroCoreApiTypes.UpdateCompanyMemberRole
    );
    setShouldShowDialog(true);
  }

  return (
    <>
      <FaroTextMenuButton
        buttonTitle="Change role"
        tooltipText="Change role in workspace"
        buttonSx={() => {
          return { fontWeight: 600 };
        }}
      >
        {assignableRoles.map((role) => (
          <FaroMenuItem
            data-my-value={role}
            key={role}
            value={role}
            onClick={onNewRoleClicked}
            sx={getRoleMenuItemSx({ isTableCell: false })}
          >
            <RoleMenuItem role={role} />
          </FaroMenuItem>
        ))}
      </FaroTextMenuButton>

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "changeRole" && shouldShowSuccessDialog && (
        <BulkSuccessDialog dialogContent={successDialogContent} />
      )}

      {bulkActionName === "changeRole" && shouldShowDialog && (
        <FaroBulkDialog
          dialogContent={bulkDialogContent}
          onClose={() => setShouldShowDialog(false)}
          trackingEvent={BulkActionEvents.bulkChangeRole}
          bulkActionCallback={(member: SdbMemberTypes) =>
            dispatch(
              updateMemberRoleInWorkspace({
                companyId,
                role: newRole,
                identity: member.identity,
              })
            ).unwrap()
          }
          selectedEntities={selectedEntities}
          uniqueIdKey="identity"
          tableDataType={entityType}
        />
      )}
    </>
  );
}
